// Mock data for Get Lucid - Using ONLY user-provided images where specified

export const images = {
  logo: '/logo.png',
  heroBackground: '/hero-homepage.png',
  aboutTara: '/about-tara.png',
  privateHypnotherapy: '/private-hypnotherapy.png',
  corporatePrograms: '/corporate-programs.png',
  transformationalHealing: '/transformational-healing.png',
  mindsetCoaching: '/mindset-coaching.png',
  fengShui: '/feng-shui.png',
  meditationCircle: '/meditation-circle.jpg'
};

export const calendlyLink = 'https://calendly.com/getlucidwithtaraphillips';

export const services = [
  {
    id: 'private-hypnotherapy',
    title: 'Private Hypnotherapy',
    shortDescription: 'One-to-One Subconscious Work, Designed Around You',
    image: images.privateHypnotherapy,
    content: {
      intro: 'If you have been trying to think your way out of something — and it is not shifting — the pattern is likely deeper. Private hypnotherapy at Get Lucid is structured, individualized, and clinically informed. This is not motivational coaching. It is targeted subconscious work designed to change the conditioned responses driving stress, fear, habits, and performance blocks.',
      features: [
        'Clinical hypnotherapy',
        'Psychotherapy-informed frameworks', 
        'Neuroscience-based understanding of memory and conditioning',
        'EMDR-informed trauma processing',
        'Breathwork for nervous system regulation',
        'Somatic awareness and body-based integration',
        'Inner child therapy',
        'Future progression and mental rehearsal',
        'Past life regression (experience-based, when appropriate)',
        'Sound-based nervous system support'
      ],
      options: {
        single: {
          title: 'Single Session',
          subtitle: 'A Clear, Focused Breakthrough',
          description: 'Best for one specific trigger or event, confidence resets, pre-performance support, clarity on a repeating pattern, targeted breakthroughs.'
        },
        structured: {
          title: 'Structured Program',
          subtitle: 'Progressive, Reinforced Change',
          description: 'Best for long-standing anxiety cycles, trauma-informed work, repeated emotional triggers, habit change, sleep regulation, performance conditioning. Typically includes 3-8 sessions.'
        }
      },
      successRate: '87% of clients report significant improvement within 3–5 sessions.',
      note: 'Hypnotherapy is used as a complementary mind–body approach and does not replace medical or psychiatric care.'
    }
  },
  {
    id: 'corporate-programs',
    title: 'Corporate Programs',
    shortDescription: 'For Leaders, Founders & High-Performing Teams',
    image: images.corporatePrograms,
    content: {
      intro: 'Performance begins in the mind. Sustainable excellence begins in the nervous system. Elite Subconscious Performance Architecture is a strategic recalibration framework designed for organizations operating at high velocity — where clarity, resilience, and emotional regulation are not optional.',
      crisis: {
        title: 'The Modern Workplace Crisis',
        stats: [
          '86% of Indian employees report struggling or suffering at work',
          '76% say stress directly impacts performance',
          '57% cite burnout and emotional exhaustion as primary barriers'
        ]
      },
      programs: [
        {
          name: 'LUCID RESET',
          duration: '90-Minute Strategic Recalibration',
          ideal: 'Leadership meetings, strategy off-sites, high-performance cycles',
          outcomes: ['Immediate nervous system regulation', 'Composure under pressure', 'Improved meeting clarity']
        },
        {
          name: 'LUCID SHIFT',
          duration: 'Half-Day Performance Acceleration',
          ideal: 'Leadership teams, managers navigating complexity',
          outcomes: ['Increased cognitive clarity', 'Emotional regulation tools', 'Creativity expansion']
        },
        {
          name: 'LUCID IMMERSION',
          duration: 'Full-Day Strategic Performance Architecture',
          ideal: 'Executive teams, growth-stage organizations',
          outcomes: ['Strategic clarity', 'Reduced cognitive overload', 'Sustainable high-performance tools']
        },
        {
          name: 'CONSCIOUS LEADERS',
          duration: 'Two-Day Executive Immersion',
          ideal: 'Senior executives and founders',
          outcomes: ['Leadership presence', 'Communication precision', 'Decision velocity']
        }
      ],
      contact: {
        email: 'getlucidwithtaraphillips@gmail.com',
        phone: '+91 9008 455 435'
      }
    }
  },
  {
    id: 'transformational-healing',
    title: 'Transformational Healing',
    shortDescription: 'Deep Spiritual & Subconscious Integration',
    image: images.transformationalHealing,
    content: {
      intro: 'Transformational Healing is designed for individuals who feel called to deeper inner work — beyond symptom relief, beyond habit change. This is for those navigating awakening, identity shifts, spiritual curiosity, emotional release, or a desire to understand themselves at a more profound level.',
      approach: [
        'Breathwork for nervous system regulation',
        'Somatic movement and body-based awareness',
        'Inner child therapy',
        'Past life regression (experience-based, when appropriate)',
        'Guided meditation journeys',
        'Higher self exploration',
        'Sound-based nervous system recalibration',
        'Womb regression work',
        'Energy-based regulation inspired by Qi Gong'
      ],
      whoItsFor: [
        'Feel they are going through awakening or internal change',
        'Experience recurring emotional themes they cannot explain',
        'Want to reconnect with intuition and self-trust',
        'Feel disconnected from purpose or meaning',
        'Are curious about consciousness but want structure',
        'Seek deeper emotional release beyond talk therapy'
      ]
    }
  },
  {
    id: 'mindset-coaching',
    title: 'Mindset Coaching',
    shortDescription: 'Ongoing Strategic Support for Life, Work & Performance',
    image: images.mindsetCoaching,
    content: {
      intro: 'Mindset Coaching at Get Lucid is a monthly subscription designed for people who want consistent clarity, direction, and accountability. This is not therapy. It is not vague motivation. It is structured, high-level thinking support for individuals who want to perform better, make stronger decisions, and build momentum.',
      whatThisIs: [
        'Strategic life coaching',
        'Behavioral pattern analysis',
        'Accountability and forward planning',
        'Nervous system regulation tools for performance',
        'Clear decision-making frameworks'
      ],
      sessions: [
        'Performance optimization',
        'Confidence calibration',
        'Decision-making clarity',
        'Habit restructuring',
        'Productivity systems',
        'Communication strategy',
        'Relationship navigation',
        'Stress management'
      ],
      whoItsFor: [
        'Professionals navigating high-pressure roles',
        'Entrepreneurs building something meaningful',
        'Individuals in transition',
        'People with big goals but inconsistent execution',
        'High performers who want sharper thinking'
      ]
    }
  },
  {
    id: 'feng-shui-guidance',
    title: 'Feng Shui Guidance',
    shortDescription: 'Spatial Intelligence for Home & Office',
    image: images.fengShui,
    content: {
      intro: 'Feng Shui is the art and science of spatial alignment. Originating over 3,000 years ago in ancient China, it examines how layout, orientation, light, materials, and spatial flow influence human behavior, emotion, and clarity.',
      philosophy: 'Your environment is not neutral. It is either supporting you — or subtly working against you.',
      forHome: [
        'Entrance alignment (first point of energy entry)',
        'Bedroom positioning for sleep quality',
        'Living areas for relational harmony',
        'Kitchen flow and nourishment symbolism',
        'Decluttering for mental clarity',
        'Material, texture, and lighting balance'
      ],
      forOffice: [
        'Leadership positioning and authority placement',
        'Desk alignment for confidence and control',
        'Clear lines of sight to reduce stress',
        'Flow pathways that reduce friction',
        'Lighting optimization for cognitive focus',
        'Spatial organization to enhance productivity'
      ]
    }
  }
];

export const testimonials = [
  {
    id: 1,
    quote: "Hypnotherapy is the most powerful mind science you've never heard of — almost a secret I hesitate to share, because that is just how powerful and effective it is. I started working with Tara last year and my life has changed since then. I have healed traumas I didn't even know existed, overcome habits I had been trying to break for years, elevated my performance in every area of my work, and manifested my goals with precision rather than trial and error. There is a science to achieving your dreams, a science to overcoming your habits, and a science to healing — and Tara has helped me access the subconscious mind through hypnotherapy to achieve everything I've ever wanted. Thank you, Tara. I highly recommend hypnotherapy and working with Tara for anyone who wants to change any aspect of their life — whether it's healing, stress management, achieving your goals, or improving your performance. Anything you can think of can be addressed through hypnotherapy in a fast and effective way.",
    author: 'Bhavna M',
    initials: 'BM'
  },
  {
    id: 2,
    quote: "I'd like to take a moment to talk about my experience working with Tara. I had the pleasure of attending a few sessions, and having known her for a while, I was naturally intrigued when she began her journey with hypnotherapy. Like most people, I came in with certain misconceptions about what hypnotherapy actually is — but it's nothing like the narrative many of us have been exposed to. Those fears and misconceptions were quickly put to rest in my first session. I work in the creative field in Dubai, and as exciting as that sounds, at the end of the day we're all part of the corporate rat race. We're constantly overstimulated with information and rarely get the chance to pause and process what we're going through or why we're feeling a certain way. I used to experience random bursts of burnout — brain fog, anxiety out of nowhere, a lack of motivation. The problem was that without getting to the root of it, I was just burying everything and pushing forward, which isn't really a solution. What hypnotherapy did for me was help me understand the source of my stress and bring more balance into my life. It's an ongoing process, but I've already noticed a significant shift in how I approach my daily life and my work. Creative blocks and mental walls that I didn't even realise were there have slowly started to dissolve. I feel more liberated — in my mind, my body, and my soul. The most important thing is going into it with an open mind. I trusted Tara completely, and that openness allowed me to embrace what I was going through, understand it, and heal from it. It's something you have to give a real chance — but when you do, the results speak for themselves.",
    author: 'Karthik M',
    initials: 'KM'
  },
  {
    id: 3,
    quote: "When I first started working with Tara, I was in a pretty low place. I was constantly doubting myself, overthinking every move, and being far too hard on myself. I knew I had potential, but I couldn't seem to access it. I began with talk therapy and some energy work, and then Tara introduced me to hypnotherapy — and that's where everything truly shifted. It helped me rewire the way I thought about myself. Gradually, all that negative self-talk began to fade, and I didn't even notice until I woke up one day and it simply wasn't there anymore. Through these sessions, I learned how to set goals, visualise success, and actually believe I could achieve it. My mindset shifted completely — from \"I can't\" to \"of course I can.\" I started showing up differently at work. I was more confident, more focused, and people noticed. I began to be recognised for my ideas, received a promotion, and found myself connecting with people on a whole new level. My energy changed, and the world around me changed with it. Over time, I became more open, even met the love of my life, and started enjoying life again. Working with Tara, especially through hypnotherapy, has helped me unlock a version of myself I didn't even know was possible.",
    author: 'Taslim A',
    initials: 'TA'
  },
  {
    id: 4,
    quote: "As someone working in Dubai, it's an understatement to say how stressful it can be — managing multiple projects, working with different teams, juggling deadlines, and performing at 120% all the time. It's exhausting and can very easily lead to burnout. When I first started working on my focus and mindset with Tara, and as I've continued to do so, I've noticed a real change. I feel calmer and clearer, I can manage things with much more ease, and my creativity has definitely opened up. I'm more confident with my team and in my work, and I'm able to handle more in less time. I won't say the stress disappears completely, but it definitely decreases — and more importantly, you learn to deal with it without feeling overwhelmed. Working with Tara has given me clarity, built my confidence, and helped me manage stress in a much healthier way. Thank you, Tara!",
    author: 'Sara G',
    initials: 'SG'
  },
  {
    id: 5,
    quote: "Before working with Tara, my days felt completely scattered. I'd start one thing, get pulled into another, and by the end of the day I'd be exhausted and unsatisfied with what I'd actually accomplished. Managing multiple projects at once was becoming overwhelming. After working on my structure and mindset, everything started to click. I became more organised, more intentional with my time, and my focus improved dramatically. I could multitask without burning out, and shifting between projects became so much easier. Internally, I felt less anxious, less rushed, and more in control. I started feeling genuinely proud of my work and the outcomes I was producing. My performance improved, my communication with both colleagues and loved ones got better, and I began showing up as the version of myself I had always wanted to be — clear, confident, and composed.",
    author: 'Natasha P',
    initials: 'NP'
  },
  {
    id: 6,
    quote: "Work can get intense — long hours, tight deadlines, endless emails, and the responsibility of managing a team. There were times when it felt like there was simply no space to breathe. After trying something new to reset my mind, I noticed a real change. I could focus better, stay calm even on the most stressful days, and approach challenges with confidence. It's not just about getting more done — I'm thinking more clearly, making better decisions, and leading my team without feeling overwhelmed. Even the small things that used to stress me out don't affect me the way they used to. I have more energy at the end of the day, more mental space to be creative, and I can actually enjoy the work I'm doing. It's changed the way I show up — more relaxed, more confident, and more effective — and that has made a huge difference for both me and my team.",
    author: 'Hanokh RK',
    initials: 'HR'
  },
  {
    id: 7,
    quote: "I can't thank Tara enough for helping me overcome my addiction to sugar — the stress eating and comfort eating that had been a part of my life for so many years. Her soothing voice puts you at ease instantly, and throughout every session I felt completely safe and supported. I am 58 years old and I have diabetes, and I truly believe that if I had turned to hypnotherapy three or four years ago, I may never have developed diabetes at all. It's incredible how it literally rewires the way your brain works. What I also find refreshing is that Tara uses tailored interventions rather than a standard script — she bases her approach on your personal history, your trauma, your background, your experiences with chronic pain or addiction, and that makes all the difference. It's wonderful to see hypnotherapy increasingly recognised as a complement to medical care. I hope you'll give a session with Tara a try. She is amazing.",
    author: 'Domani B',
    initials: 'DB'
  }
];

export const blogPosts = [
  {
    id: 'what-is-hypnotherapy',
    title: 'What Is Hypnotherapy?',
    subtitle: 'The Science, the History, and the Life-Changing Potential of the Mind\'s Most Powerful Tool',
    excerpt: 'There is a part of your mind that runs silently in the background — storing memories, shaping beliefs, driving habits. Hypnotherapy is one of the most powerful tools we have to access it.',
    content: `There is a part of your mind that runs silently in the background — storing memories, shaping beliefs, driving habits, and influencing nearly every decision you make. It doesn't respond to logic or willpower alone. It responds to something deeper. That part is your subconscious mind, and hypnotherapy is one of the most powerful and scientifically validated tools we have to access it.

**What Is Hypnotherapy?**

Hypnotherapy is a therapeutic technique that uses guided relaxation and focused attention to bring a person into a trance-like state — not unconscious, not asleep, but in a heightened state of inner awareness where the critical, analytical mind steps aside and the subconscious becomes receptive to positive suggestion and healing.

Think of it as opening a door to the operating system of your mind. In everyday life, our conscious mind acts as a filter, often blocking the very change we're trying to make. In hypnosis, that filter relaxes — allowing new beliefs, behaviours, and emotional patterns to be introduced at the root level.

**A Brief History**

Hypnotherapy is far from a modern invention. Its roots stretch back thousands of years. Ancient Egyptians used 'sleep temples' where patients would enter dream-like states to receive healing. In the 18th century, Franz Anton Mesmer introduced the idea of 'animal magnetism' — a precursor to modern hypnosis. Scottish surgeon James Braid later coined the term 'hypnosis' in the 1840s and was the first to study it scientifically.

By the late 19th century, pioneering neurologist Jean-Martin Charcot was using hypnosis to treat hysteria, and Sigmund Freud experimented with it in his early work. In 1958, the American Medical Association formally recognised hypnotherapy as a legitimate medical tool. Today, it is practised in hospitals, clinics, and therapeutic settings around the world.

**What the Science Says**

Modern neuroscience has given us a window into what actually happens in the brain during hypnosis. Research using fMRI imaging shows that hypnosis significantly alters activity in the prefrontal cortex — the part of the brain responsible for decision-making and self-awareness — and reduces the communication between regions associated with self-consciousness and the default mode network.

In simpler terms: the inner critic quiets, and a state of deep openness emerges.

Studies published in journals such as the International Journal of Clinical and Experimental Hypnosis have demonstrated hypnotherapy's effectiveness in reducing chronic pain, managing anxiety and depression, overcoming phobias, treating irritable bowel syndrome, aiding smoking cessation, and improving sleep quality. A Stanford University study found that approximately 25% of people are highly hypnotisable, with the majority of people responsive to some degree.

**Real Cases, Remarkable Results**

The documented outcomes of hypnotherapy are nothing short of extraordinary:

• During World War II, battlefield surgeons used hypnosis when anaesthesia wasn't available — patients underwent amputations and wound treatment without pain medication.

• In 1996, a landmark study in the journal Gut showed hypnotherapy produced significant long-term improvement in 71% of IBS patients — results that outperformed medication alone.

• Children with severe needle phobia have been guided through blood draws using hypnosis, remaining calm and cooperative throughout.

• Cancer patients have used hypnotherapy to manage chemotherapy-related nausea and anxiety so effectively that many oncology centres now offer it as standard support.

**Where and How Hypnotherapy Can Help**

The applications of hypnotherapy are remarkably wide-ranging. It has been shown to help with anxiety and panic attacks, depression and low self-worth, trauma and PTSD, phobias of all kinds, addictions and compulsive behaviours, chronic pain and psychosomatic conditions, performance anxiety, weight management, insomnia, grief, confidence, and manifesting goals with clarity and precision.

Because hypnotherapy works at the level of the subconscious, it can often achieve in a few sessions what years of talk therapy may struggle to reach. It does not bypass healing — it accelerates it.

**Is It Safe?**

Absolutely. Hypnotherapy is a gentle, non-invasive, and entirely safe practice when conducted by a trained professional. You remain in full control at all times. You cannot be made to do or say anything against your will. You are aware throughout the session and will remember what occurred. It is safe for children, teenagers, adults, and the elderly. It requires no medication and has no adverse side effects.

**Your Mind Is Waiting**

Hypnotherapy is not magic — though its results can feel miraculous. It is a science-backed, evidence-based practice that honours the profound intelligence of your mind and its ability to heal, transform, and grow.

If you have been struggling with something — whether emotional, physical, behavioural, or simply a sense that you are not living at your fullest — hypnotherapy may be exactly what you have been looking for. Your subconscious already holds the answers. Hypnotherapy simply helps you find them.`,
    image: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=800',
    date: '2024-12-01',
    author: 'Tara Phillips'
  },
  {
    id: 'what-is-regression-therapy',
    title: 'What Is Regression Therapy?',
    subtitle: 'Healing the Past to Free the Present — and Beyond',
    excerpt: 'Sometimes, no matter how hard we try to move forward, something keeps pulling us back. Regression therapy helps you find the origin of what is holding you back, so that it can finally be released.',
    content: `Sometimes, no matter how hard we try to move forward, something keeps pulling us back. A fear we can't explain. A pattern that repeats across relationships. A heaviness we've carried for as long as we can remember.

Regression therapy is the process of travelling inward — and often backward — to find the origin of what is holding you back, so that it can finally be released.

**What Is Regression Therapy?**

Regression therapy is a form of hypnotherapy that guides a person back through time — to earlier experiences in this life, to the earliest moments of existence before birth, and in some traditions, to experiences beyond this lifetime entirely.

It is based on the understanding that many of our current struggles have roots in past experiences: unprocessed emotions, decisions made in moments of fear, or wounds that were never properly tended to.

The subconscious mind stores everything. Every experience, every emotion, every moment — even those we have consciously forgotten. Regression therapy creates a safe and supported space to revisit these memories, not to relive them painfully, but to understand them, reframe them, and release the charge they hold over the present.

**Past Life Regression**

Of all the forms of regression therapy, past life regression is perhaps the most profound — and the most fascinating. It is the process of guiding a person into a deeply relaxed hypnotic state and allowing memories or impressions from other lifetimes to arise and be explored.

Whether you approach this from a spiritual perspective or a purely psychological one, the therapeutic results are often remarkable. People report vivid, detailed experiences of living in other times and places — experiences that frequently carry emotional weight directly relevant to their current life challenges.

The pioneering psychiatrist Dr. Brian Weiss, former chairman of psychiatry at the Mount Sinai Medical Centre in Miami, documented hundreds of past life regression cases in his landmark book 'Many Lives, Many Masters.' His patient Catherine, under hypnosis, recounted detailed accounts of past lives — including information she could not have known — and her lifelong anxiety and phobias resolved completely as a result.

**Womb Regression**

Before we are born, we are already feeling. Research in prenatal psychology has shown that the foetus is emotionally aware and absorbs information from its environment in the womb. A mother's stress, emotional state, or the atmosphere of the pregnancy can create imprints that affect a person well into adult life.

Womb regression is the practice of guiding a person back to the experience of being in the womb — and sometimes to the moment of conception itself. People frequently access feelings of being unwanted, arriving during a period of family crisis, or absorbing a mother's grief or anxiety.

When these early imprints are brought into conscious awareness and compassionately reframed, profound healing often follows.

**This Life Regression**

Even within a single lifetime, there is often much to return to and heal. Regression therapy can guide a person back to pivotal childhood moments, to experiences of loss or abandonment, to the first time a limiting belief was formed.

Often these memories are not dramatic — they are quiet moments of misunderstanding, of a child concluding they were not enough, not loved, not safe. Revisiting these experiences through the compassionate, adult eyes of the hypnotic state allows them to be understood differently, reframed with kindness, and released with love.

**Who Is It For?**

Regression therapy is for anyone who feels that something from the past — known or unknown, in this life or beyond — is influencing their present. It is for those who carry anxiety without a clear cause, who repeat patterns in relationships, who experience unexplained fears, chronic grief, or a persistent sense of disconnection from life.

It is a deeply gentle process, conducted with full support and care. You are never alone in what arises. And what arises, in the hands of a skilled therapist, becomes the very medicine that heals.`,
    image: 'https://images.unsplash.com/photo-1518241353330-0f7941c2d9b5?w=800',
    date: '2024-11-28',
    author: 'Tara Phillips'
  },
  {
    id: 'how-breathwork-changes-your-life',
    title: 'How Can Breathwork Change Your Life?',
    subtitle: 'The Science of Using Your Breath to Heal, Rewire, and Transform',
    excerpt: 'What if the way you breathe has been quietly shaping your nervous system, your emotional landscape, and your mental health — and what if changing it could change everything?',
    content: `You have been breathing your entire life without thinking about it. But what if the way you breathe has been quietly shaping your nervous system, your emotional landscape, and your mental health — and what if changing it could change everything?

Breathwork is not simply deep breathing. It is a conscious, intentional practice that uses specific breathing patterns to alter your physiology, access stored emotions, and initiate profound healing.

**The Science of the Breath**

Your breath is the only autonomic function in your body that you can also consciously control. This makes it a unique bridge between your voluntary and involuntary nervous systems — and a direct pathway to the parts of your body and brain that hold trauma, stress, and emotional memory.

When you breathe in a certain way, you directly influence your vagus nerve — the long wandering nerve that regulates your heart rate, digestion, immune response, and emotional state.

Slow, deep breathing activates the parasympathetic nervous system (your rest-and-repair state), reducing cortisol and adrenaline, lowering blood pressure, and creating a physiological sense of safety.

Rapid, rhythmic breathing patterns — used in practices like holotropic breathwork — do something entirely different: they temporarily shift blood chemistry, alter brain activity, and can facilitate access to non-ordinary states of consciousness where deep emotional processing becomes possible.

**How Breathwork Breaks Emotional Patterns**

Trauma and unprocessed emotion are not stored only in the mind — they are stored in the body. The body keeps the score, as psychiatrist Dr. Bessel van der Kolk famously wrote. Tension in the chest, a constricted throat, shallow breathing that has become habitual — these are the physical signatures of unexpressed emotion and past pain.

Breathwork works at this bodily level. By consciously altering the rhythm and depth of the breath, we begin to unlock what the body has been holding.

During an active breathwork session, many people experience the spontaneous release of emotion — tears, laughter, shaking, sounds — not because they are forced, but because the body finally feels safe enough to let go. This is not a breakdown. It is a breakthrough.

**Rewiring the Brain**

Breathwork has been shown to create measurable changes in brain structure and function. Studies on mindfulness-based breathing practices have found increased grey matter density in regions associated with self-awareness, compassion, and emotional regulation.

Breathwork also promotes neuroplasticity — the brain's ability to form new neural connections. By repeatedly activating calmer, more centred states through the breath, you quite literally rewire the brain to default to those states more readily.

Over time, reactions that once triggered anxiety, anger, or overwhelm begin to lose their grip.

**What to Expect**

A breathwork session can take many forms. Some are gentle and meditative, using slow diaphragmatic breathing to cultivate presence and reduce stress. Others are more active — using circular breathing patterns sustained over 30 to 60 minutes to produce profound inner experiences.

Many people describe sensations of warmth, tingling, emotional release, vivid imagery, or a deep sense of unity and peace. No experience is the same. And that is exactly the point — breathwork meets you exactly where you are.

**Your Breath Is Your Birthright**

Of all the tools available for healing and transformation, the breath is the one you carry with you always. It costs nothing. It requires no equipment. And it is, quite possibly, the most direct route to your own inner healing that exists.

Every breath is an opportunity. And every intentional breath is a step toward becoming more fully, freely, magnificently yourself.`,
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800',
    date: '2024-11-25',
    author: 'Tara Phillips'
  },
  {
    id: 'chakra-balancing',
    title: 'Chakra Balancing: The Ancient Science of Energy',
    subtitle: 'How Energy Moves Through You, Between You, and Into Everything You Touch',
    excerpt: 'There is more to you than your physical body. Modern physics has confirmed what ancient wisdom traditions have known for millennia: everything is energy.',
    content: `There is more to you than your physical body. Modern physics has confirmed what ancient wisdom traditions have known for millennia: everything is energy. You are not a solid, fixed object moving through a solid, fixed world. You are a dynamic, vibrating field of energy — and the quality of that energy affects your health, your emotions, your relationships, and your reality.

**Chakras and the Energy Body**

The chakra system originates in ancient Indian texts — the Vedas — dating back over 3,000 years. The word 'chakra' is Sanskrit for 'wheel,' referring to the spinning vortices of energy that exist within and around the body.

There are seven primary chakras, each located along the spine, each associated with specific physical organs, emotional states, and areas of life:

• The root chakra at the base of the spine governs safety and belonging
• The sacral chakra holds creativity and sensuality
• The solar plexus is the seat of personal power
• The heart chakra is the centre of love and connection
• The throat chakra governs authentic expression
• The third eye is associated with intuition and inner vision
• The crown chakra connects us to something greater than ourselves

When these energy centres are balanced and flowing freely, we experience vitality, emotional equilibrium, and a sense of aliveness. When they are blocked or imbalanced — through stress, trauma, repressed emotion, or disconnection — we may experience physical symptoms, emotional heaviness, or a persistent sense that something is off.

**Qi: The Life Force That Flows Through All Things**

In Chinese medicine, this same essential energy is called Qi (pronounced 'chee'). Qi is the life force that animates all living beings, flowing through the body along pathways called meridians. When Qi flows freely, health and harmony result. When it stagnates or becomes disrupted, illness follows.

Research in biophysics has identified electrical fields along the body's meridian pathways, and studies have shown that acupuncture produces measurable changes in brain activity, immune function, and pain modulation.

**We Transmit Energy to People, Places, and Things**

Quantum physics describes a universe in which particles remain connected across space, where observation itself influences outcome, and where the boundaries between 'self' and 'environment' are far less fixed than we assume.

Research from the HeartMath Institute has demonstrated that the heart generates an electromagnetic field that extends several feet beyond the body — and that this field changes in response to our emotional states. When we feel love, gratitude, or compassion, our heart field becomes coherent and harmonious. When we feel stress, anger, or fear, it becomes erratic.

Every interaction you have is an energetic exchange. Every space you inhabit carries the residue of the energy within it.

**Balancing Your Energy**

Chakra balancing can be facilitated through a range of practices: energy healing such as Reiki, sound healing with tuning forks or singing bowls, guided visualisation, breathwork, movement, and the deep inner work of hypnotherapy and meditation.

When your energy body is clear and aligned, you move through the world differently. You feel more grounded, more open, more magnetic. You attract experiences that match your frequency. And you begin to understand that healing is not separate from spirituality — they have always been the same thing.`,
    image: 'https://images.unsplash.com/photo-1545389336-cf090694435e?w=800',
    date: '2024-11-20',
    author: 'Tara Phillips'
  },
  {
    id: 'how-to-manifest',
    title: 'How to Manifest Anything You Want',
    subtitle: 'The Science and Spirituality of Intention, Emotion, and Action',
    excerpt: 'Manifestation is real, it is scientifically explainable, and it is a skill that anyone can learn. Learn how intention, emotion, and action work together to create your reality.',
    content: `Manifestation has become a popular word — sometimes dismissed as wishful thinking, sometimes described in almost mystical terms that feel out of reach. The truth is both more grounded and more extraordinary than either extreme suggests. Manifestation is real, it is scientifically explainable, and it is a skill that anyone can learn.

**What Manifestation Actually Is**

At its core, manifestation is the process of bringing something from the realm of thought and feeling into the realm of physical reality. It is not passive. It is not about staring at a vision board and waiting for the universe to deliver. It is an active, intentional, emotionally engaged practice that works with the fundamental laws of how the mind and reality interact.

Every thought creates a neurological pattern. Every emotion sends a chemical signal through the body. Every repeated belief shapes the lens through which we perceive and interact with the world — and therefore the opportunities we notice, the actions we take, and the results we produce.

**The Role of Intention**

Intention is the seed of manifestation. It is the clear, specific decision about what you want to call into your life — not a vague wish, but a defined, committed direction.

Research in quantum physics and consciousness studies suggests that intention has measurable effects on physical reality. Dr. William Tiller of Stanford University conducted decades of experiments demonstrating that focused human intention could alter the molecular structure of water and influence biological systems.

Setting a clear intention begins the process of directing your subconscious resources toward a specific outcome. Your reticular activating system — the brain's filter — begins to highlight opportunities, connections, and information that align with your stated desire.

**The Role of Emotion**

Intention without emotion is a map without a vehicle. Emotion is the energy that fuels manifestation. This is why simply thinking about what you want is rarely sufficient — you need to feel it.

The heart generates the body's strongest electromagnetic field, and research from the HeartMath Institute shows that positive emotional states — gratitude, love, excitement, joy — create a coherent heart rhythm that synchronises the brain and body.

This is why visualisation is so powerful when done with emotional depth. The subconscious mind does not distinguish between a vividly imagined experience and a real one. When you imagine your desired outcome with full sensory detail and genuine feeling, your brain and body respond as though it is already happening.

**The Role of Action**

Manifestation without action is fantasy. The universe responds to movement. When your intention is clear and your emotional state is aligned, inspired action becomes natural.

The key is to act from a place of alignment rather than desperation, from certainty rather than scarcity. Aligned action is the synchronised, purposeful movement of someone who trusts their direction and their capacity.

**Removing the Blocks**

Perhaps the most important element of manifestation is clearing the subconscious blocks that work against it. If, beneath your conscious desire for success, there lives a belief that you don't deserve it — those beliefs will consistently undermine your efforts.

This is where hypnotherapy becomes one of the most powerful manifestation tools available. By working directly with the subconscious, hypnotherapy can identify and dissolve these hidden patterns, allowing your conscious intentions and your subconscious programming to finally work in the same direction.

When intention, emotion, action, and a clear subconscious are all aligned, manifestation is not a mystery. It is an inevitability.`,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800',
    date: '2024-11-15',
    author: 'Tara Phillips'
  },
  {
    id: 'feng-shui-basics',
    title: 'Feng Shui Basics: The Art of Living in Harmony',
    subtitle: 'How the Energy of Your Environment Shapes the Energy of Your Life',
    excerpt: 'Your environment is not neutral. It is in constant energetic dialogue with you, and the quality of that dialogue affects every area of your life.',
    content: `Look around the space you are in right now. How does it make you feel? Calm or cluttered? Open or oppressive? Inspired or drained?

The ancient Chinese art of Feng Shui is built on a fundamental truth that most of us sense intuitively but rarely examine consciously: our environment is not neutral. It is in constant energetic dialogue with us, and the quality of that dialogue affects every area of our life.

**What Is Feng Shui?**

Feng Shui, which translates as 'wind and water,' is a 3,000-year-old Chinese system for arranging environments in harmony with the natural flow of energy — Qi. Just as Qi flows through the human body along meridians, it flows through our homes and workplaces along invisible pathways.

When that flow is balanced and unobstructed, the people within the space tend to thrive. When it is blocked, chaotic, or stagnant, the effects can manifest as stress, illness, conflict, stalled progress, or a persistent sense of unease.

**The Bagua: A Map of Your Life**

Central to Feng Shui practice is the Bagua — an eight-sided map that divides your home into nine zones, each corresponding to a different area of life:

• Wealth and abundance
• Fame and reputation  
• Love and partnership
• Family and ancestry
• Health (at the centre)
• Creativity and children
• Knowledge and self-cultivation
• Career and life path
• Helpful people and travel

By overlaying this map onto your floor plan, a Feng Shui practitioner can identify which areas of your home correspond to which areas of your life, and make intentional adjustments to enhance the flow of Qi.

**The Five Elements**

Feng Shui works extensively with the five elements — wood, fire, earth, metal, and water — and the relationships between them. Each element carries specific qualities:

• Wood represents growth and vitality
• Fire represents passion and visibility
• Earth represents stability and nourishment
• Metal represents clarity and precision
• Water represents wisdom and flow

A balanced space incorporates all five in some form. An overabundance of any one element — or a complete absence — creates imbalance.

**Simple Principles to Begin With**

You do not need to overhaul your entire home to begin working with Feng Shui:

• Clear your clutter — clutter is stagnant energy
• Let in natural light wherever possible
• Position your bed, desk, and sofa in the 'command position' — where you can see the door without being directly in line with it
• Remove or repair anything that is broken
• Introduce living plants, which generate fresh Qi

Above all, Feng Shui invites you to become conscious of your environment — to stop moving through your home on autopilot and start feeling into what each space is communicating.

Your home is a mirror of your inner world. When you tend to one, you inevitably tend to the other.`,
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800',
    date: '2024-11-10',
    author: 'Tara Phillips'
  },
  {
    id: 'psychotherapy-vs-hypnotherapy',
    title: 'Psychotherapy vs Hypnotherapy: What Is the Difference?',
    subtitle: 'Understanding Two Powerful Paths to Healing — and How They Work Together',
    excerpt: 'Both are legitimate, evidence-based therapeutic modalities. Both aim to support wellbeing. But they work in fundamentally different ways.',
    content: `When people seek support for emotional struggles, mental health challenges, or patterns they want to change, they are often faced with a range of options that can feel confusing or overwhelming. Two of the most well-known are psychotherapy and hypnotherapy.

Both are legitimate, evidence-based therapeutic modalities. Both aim to support wellbeing and personal transformation. But they work in fundamentally different ways — and understanding the distinction can help you choose the approach that is right for you.

**What Is Psychotherapy?**

Psychotherapy is an umbrella term for a wide range of talking therapies — approaches that use dialogue, reflection, and the therapeutic relationship to promote psychological wellbeing. It includes cognitive behavioural therapy (CBT), psychodynamic therapy, person-centred counselling, EMDR, and many others.

Psychotherapy primarily works with the conscious mind. Through conversation, a therapist helps a client identify thought patterns, understand the origins of emotional responses, develop coping strategies, and gradually build new perspectives and behaviours.

**What Is Hypnotherapy?**

Hypnotherapy bypasses the conscious mind and works directly with the subconscious. Rather than talking about a problem and analysing it from a conscious perspective, hypnotherapy guides the client into a deeply relaxed, focused state where the subconscious mind becomes accessible and receptive to change.

The subconscious is where our deepest beliefs, habits, emotional patterns, and memories are stored. Many of the issues people bring to therapy — anxiety, low self-worth, phobias, addictions, trauma responses — are rooted at this subconscious level.

**The Key Differences**

The most significant difference lies in the level of mind being engaged:

• Psychotherapy works primarily at the conscious level — using language, reason, and reflection
• Hypnotherapy works at the subconscious level — using imagery, suggestion, and the body's own relaxation response

This means that psychotherapy often requires more time. Understanding a pattern intellectually is one thing; changing the subconscious programming that drives it is another. Hypnotherapy can frequently achieve significant, lasting results in fewer sessions, because it addresses the root cause rather than the symptom.

**How They Complement Each Other**

These modalities are not mutually exclusive — in fact, they can work beautifully together. Many practitioners integrate elements of both.

A client may benefit from psychotherapy to develop conscious self-awareness and build the therapeutic relationship, alongside hypnotherapy to anchor change at the subconscious level.

Some practitioners, trained in both disciplines, move fluidly between conscious-level conversation and hypnotic work within a single session — a holistic approach that honours the full spectrum of the human mind.

**Which Is Right for You?**

If you are seeking ongoing support and value the gradual building of insight over time, psychotherapy may be a wonderful fit.

If you are looking for focused, effective change — whether to overcome a specific issue, shift a limiting belief, heal a trauma, or accelerate personal growth — hypnotherapy may offer exactly what you need.

And if what you are truly seeking is comprehensive, deep, and lasting transformation, the integration of both may be your most powerful path.

The mind is not one-dimensional. Why should your healing be?`,
    image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800',
    date: '2024-11-05',
    author: 'Tara Phillips'
  },
  {
    id: 'facing-your-fears',
    title: 'Facing Your Fears: How Hypnotherapy Frees You From Phobias',
    subtitle: 'The Science of Fear, the Reality of Phobias, and the Profound Power of the Subconscious Mind to Heal Them',
    excerpt: 'Phobias do not live in the conscious mind. They live in the subconscious. And that is precisely where hypnotherapy works.',
    content: `Fear is one of the most primitive and powerful forces in the human experience. In the right context, it protects us. But when fear becomes disconnected from genuine threat — when it attaches itself to flying, or spiders, or social situations — it stops being a protector and starts being a prison.

Phobias affect hundreds of millions of people worldwide. They are not a sign of weakness. They are not something you can simply think your way out of. Because phobias do not live in the conscious mind. They live in the subconscious. And that is precisely where hypnotherapy works.

**What Is a Phobia?**

A phobia is an intense, irrational, and persistent fear of a specific object, situation, or experience that poses little or no real danger. The word 'irrational' is important here — not because the fear feels irrational (it doesn't; it feels completely real) — but because the cognitive mind cannot override it through reason alone.

Common phobias include:

• Arachnophobia (spiders)
• Aviophobia (flying)
• Claustrophobia (enclosed spaces)
• Social phobia
• Emetophobia (vomiting)
• Haemophobia (blood)

Less commonly discussed — but profoundly impactful — is thanatophobia: the fear of death. This is one of the most pervasive fears in the human experience, and it underlies a remarkable range of anxiety presentations.

**How Hypnotherapy Treats Phobias**

Phobias are subconscious responses — automatic, fast, and bypassing the rational mind entirely. They were formed through a sensitising event: a moment when the nervous system registered a threat and created an association between a stimulus and danger.

Hypnotherapy works by guiding the client into a deeply relaxed state where the subconscious mind becomes accessible. In this state, the therapist can work with the original sensitising event and begin to reprocess the emotional association.

Techniques such as regression to cause, the rewind technique, systematic desensitisation under hypnosis, and direct subconscious suggestion are used to disrupt the fear response at its root.

The results can be extraordinary. Studies have demonstrated hypnotherapy's effectiveness in treating specific phobias, with success rates significantly exceeding those of cognitive behavioural therapy alone. Many clients experience significant relief within one to three sessions.

**You Do Not Have to Live Around Your Fear**

Avoidance is the natural response to a phobia — and it is also the thing that maintains and strengthens it. Every time you avoid the feared stimulus, the subconscious receives confirmation that the threat is real.

Hypnotherapy offers a different possibility. Not managing your fear — but resolving it. Not building your life around what you cannot do — but stepping into the full breadth of what you can.

If you are ready to explore what life looks like on the other side of a phobia, a conversation with Tara at Get Lucid is a powerful place to begin.`,
    image: 'https://images.unsplash.com/photo-1509909756405-be0199881695?w=800',
    date: '2024-10-30',
    author: 'Tara Phillips'
  },
  {
    id: 'why-willpower-wont-make-you-stop',
    title: 'Why Willpower Alone Won\'t Make You Stop',
    subtitle: 'Understanding the Subconscious Architecture of Addiction — and the Most Effective Tool for Dismantling It',
    excerpt: 'Habits and addictions are not simply conscious choices. They are subconscious programmes that the brain has automated. Hypnotherapy works at the source.',
    content: `You have tried to stop. Perhaps many times. You know it is harmful. You know the reasons to quit are compelling. And yet — something keeps pulling you back.

Not logic. Not willpower. Something deeper and more powerful than either. That something is your subconscious mind.

**Why Habits and Addictions Are So Hard to Break**

Habits — including smoking, vaping, alcohol reliance, and other substance patterns — are not simply conscious choices repeated over time. They are subconscious programmes: deeply encoded behavioural loops that the brain has automated.

Once a behaviour has been performed enough times in a consistent context, the brain moves it from conscious control to subconscious execution. This is why you can light a cigarette or reach for a substance without having consciously decided to.

Layered beneath the habit itself are often deeper subconscious associations: the belief that the substance manages stress, that it provides comfort, that it is part of your identity.

**Smoking: One of the Most Researched Applications**

Smoking cessation is one of the most extensively researched applications of clinical hypnotherapy — and the results are compelling.

A meta-analysis published in the Journal of Applied Psychology reviewing 600 studies and over 72,000 participants found hypnosis to be three times more effective than nicotine replacement therapy and fifteen times more effective than trying to quit without assistance.

Hypnotherapy works for smoking cessation because it addresses the habit at the subconscious level where it lives. Rather than relying on willpower to override an automated programme, hypnotherapy reprograms the association itself.

**Alcohol and Substance Habits**

Hypnotherapy is also highly effective for addressing habitual alcohol use, binge eating, sugar dependency, vaping, and cannabis reliance.

In each case, the process involves:

• Identifying the subconscious triggers and emotional associations driving the behaviour
• Accessing the root cause — often an unmet emotional need
• Reprocessing that association at the subconscious level
• Strengthening self-efficacy and establishing a new subconscious identity

**What to Expect**

A typical programme for smoking cessation at Get Lucid can be completed in one to three sessions for many clients, with remarkable and lasting results. More complex substance patterns may require a longer structured journey.

If you have been trying to stop and finding that trying is not enough — it may not be a question of willpower. It may be a question of the right tool.

Hypnotherapy is that tool.`,
    image: 'https://images.unsplash.com/photo-1555685812-4b943f1cb0eb?w=800',
    date: '2024-10-25',
    author: 'Tara Phillips'
  },
  {
    id: 'why-not-making-money',
    title: 'Why You\'re Not Making the Money You Should Be',
    subtitle: 'The Subconscious Architecture of Financial Limitation — and How to Dismantle It',
    excerpt: 'The problem is almost certainly not your strategy. It is your subconscious programming around money, success, and what you believe you are allowed to have.',
    content: `You are talented. You work hard. You have done the courses, built the skills, shown up consistently. And yet — the financial results do not match the effort.

Here is what most financial advice will never tell you: the problem is almost certainly not your strategy. It is your subconscious programming around money, success, and what you believe — at the deepest level — you are allowed to have.

**The Scarcity Mindset: Where It Comes From**

By the time we reach adulthood, most of us are operating from a financial blueprint we did not consciously choose. It was formed in childhood, assembled from the conversations we overheard, the emotional charge that surrounded money in our family, and the conclusions we drew from our early experiences.

If money was associated with stress, conflict, scarcity, or shame in your environment growing up — your subconscious encoded those associations as truth. And now, decades later, those beliefs are quietly running your financial life.

**Success Thresholds: The Invisible Ceiling**

One of the most insidious mechanisms of subconscious financial limitation is the 'upper limit problem' — the tendency to unconsciously sabotage progress the moment we approach our internal 'success threshold.'

This sabotage rarely looks obvious. It looks like a sudden illness that derails an opportunity. A conflict with a key collaborator at a crucial moment. Procrastination that arrives precisely when momentum is building.

These are not coincidences — they are the subconscious maintaining the familiar ceiling.

**The Dysregulated Nervous System**

There is a direct relationship between nervous system regulation and financial performance. When the nervous system is chronically dysregulated:

• Cognitive bandwidth narrows
• Risk tolerance drops
• The window of opportunity perception shrinks
• Energy communicates desperation rather than confidence

**Worthiness: The Root of Everything**

Beneath all of it, there is almost always one foundational belief: I am not worth it. Not worthy of abundance. Not worthy of ease. Not worthy of being paid what I am truly worth.

This belief is not rational. It is subconscious. Affirmations cannot reach it. But hypnotherapy can access it directly — and change it at the source.

**How Hypnotherapy Rewrites Your Financial Blueprint**

Hypnotherapy for financial transformation works by:

• Regression work to identify formative experiences
• Direct suggestion to install new beliefs around worthiness
• Nervous system regulation to shift chronic stress
• NLP techniques to change the internal representation of money and success

Combined with Mindset Coaching for strategic accountability, this represents one of the most comprehensive approaches to financial transformation available.

You were not born to struggle. The financial ceiling you are bumping against is made of belief. And belief can be changed.`,
    image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800',
    date: '2024-10-20',
    author: 'Tara Phillips'
  }
];

export const events = [
  {
    id: 1,
    title: 'Sacred Reset Retreat',
    date: 'Coming Soon',
    time: 'TBA',
    location: 'Location to be announced',
    description: 'A deeply held, transformational retreat for those ready to reset their nervous system, subconscious patterns, and inner alignment. This is not a wellness getaway. It is structured inner work — in an intimate, carefully curated container.',
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800',
    price: 'TBA',
    status: 'coming-soon'
  },
  {
    id: 2,
    title: 'Sunday Meditation Circle',
    date: 'Every Sunday',
    time: 'Check WhatsApp group',
    location: 'Cubbon Park, Bangalore',
    description: 'An open community space to reset your week. Each Sunday, we gather in nature to regulate the nervous system, quiet the mind, and reconnect to clarity. No experience required. Open to ages 14+.',
    image: '/meditation-circle.jpg',
    price: 'Free',
    status: 'recurring'
  }
];

export const faqs = [
  {
    question: 'What does hypnosis feel like?',
    answer: 'Hypnosis feels like a deeply relaxed state. You are fully aware, in control, and can hear everything. Most describe it as peaceful and calming.'
  },
  {
    question: 'Is hypnotherapy safe?',
    answer: 'Yes, completely safe when conducted by a trained professional. It is a natural state you enter multiple times a day.'
  },
  {
    question: 'Will I lose control?',
    answer: 'Absolutely not. You remain in complete control throughout. Hypnosis is a state of heightened awareness.'
  },
  {
    question: 'How many sessions will I need?',
    answer: 'This varies. Some experience shifts in 1-3 sessions, while deeper work may take 6-10 sessions.'
  },
  {
    question: 'Is hypnotherapy the same as therapy?',
    answer: 'Hypnotherapy is complementary. While therapy focuses on conscious processing, hypnotherapy works with the subconscious mind.'
  },
  {
    question: 'Can everyone be hypnotized?',
    answer: 'Yes! If you can follow simple instructions and are willing to participate, you can be hypnotized.'
  },
  {
    question: 'What if I do not go under?',
    answer: 'There is no going under. Hypnosis is simply a relaxed, focused state.'
  },
  {
    question: 'What issues can hypnotherapy help with?',
    answer: 'Anxiety, stress, phobias, habits, sleep issues, confidence, performance, trauma, and personal development.'
  },
  {
    question: 'Do I need to believe in hypnosis?',
    answer: 'No, but you need to be willing to participate. What matters is your openness and desire for change.'
  },
  {
    question: 'Will I remember the session?',
    answer: 'Most people remember most or all of the session. Your subconscious is always listening.'
  },
  {
    question: 'Is it possible to get stuck in hypnosis?',
    answer: 'No, impossible. If interrupted, you would emerge naturally.'
  },
  {
    question: 'What happens in a typical session?',
    answer: 'Sessions begin with conversation, then guided relaxation, then therapeutic work. Usually 60-90 minutes.'
  },
  {
    question: 'Can hypnotherapy help with trauma?',
    answer: 'Yes, very effective. It allows processing in a safe way without reliving experiences in detail.'
  },
  {
    question: 'Do you offer virtual sessions?',
    answer: 'Yes! Virtual sessions are just as effective as in-person.'
  },
  {
    question: 'How do I prepare for my first session?',
    answer: 'Come with an open mind, comfortable clothing. Think about your goals. That is it!'
  },
  {
    question: 'Are sessions confidential?',
    answer: 'Yes, absolutely. Everything is completely confidential.'
  },
  {
    question: 'Can children be hypnotized?',
    answer: 'Yes, children respond very well. Parental consent is required.'
  },
  {
    question: 'How long do results last?',
    answer: 'Results can be permanent, especially with practice of learned tools.'
  },
  {
    question: 'What is your background?',
    answer: 'Certified hypnotherapist trained through Mindvalley, practicing student of neuroscience and psychology.'
  },
  {
    question: 'What makes your approach unique?',
    answer: 'I combine clinical hypnotherapy with psychotherapy frameworks, EMDR, breathwork, somatic practices, and sound healing.'
  }
];
